import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Chip, CircularProgress, Grid, Link, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";
import { TEST_JIGSAW_CANVAS_STORY_ID } from "./testJigsawCanvasStoryId";
import TestCanvasBoard from "./TestCanvasBoard";
import TestCanvasPieces from "./TestCanvasPieces";
import TestCanvasScatteredPair from "./TestCanvasScatteredPair";
import { buildScatterPositions } from "./TestCanvasScattered";
import TestTemplateCard from "./TestTemplateCard";
import { BOARD_SIZE, PieceVisual } from "./TestPieceVisual";

function getImageUrl(story) {
    if (!story) return null;
    return story.imageUrl ?? story.imagePreviewUrl ?? story.imageIcon400Url ?? null;
}

function getStoryTitle(story) {
    if (!story) return "";
    return story.english?.title ?? story.en?.title ?? story.french?.title ?? story.fr?.title ?? "";
}

function newBoardPieceId() {
    return `bp${Date.now().toString(36)}${Math.random().toString(36).slice(2, 9)}`;
}

const PUBLIC_PLAY_PATH = `/puzzle/play-jigsaw/${TEST_JIGSAW_CANVAS_STORY_ID}`;

/**
 * Admin copy of the two-lane jigsaw canvas (Lane 1 grid tray + board, Lane 2 scattered + board).
 * Uses {@link TEST_JIGSAW_CANVAS_STORY_ID}; public parity: {@link PUBLIC_PLAY_PATH}.
 */
export default function TestJigsawCanvasPlayPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [boardPiecesMain, setBoardPiecesMain] = useState([]);
    const [boardPiecesScatter, setBoardPiecesScatter] = useState([]);
    const [scatterPositions, setScatterPositions] = useState({});
    const [dragging, setDragging] = useState(null);
    const boardRefMain = useRef(null);
    const boardRefScatter = useRef(null);
    const trayRef = useRef(null);
    const scatteredRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await ApiService.getData(
                    `/home/jigsaw/${encodeURIComponent(TEST_JIGSAW_CANVAS_STORY_ID)}`,
                );
                if (!cancelled) {
                    setGroup(data);
                    if (data?.templates) {
                        const mediumTemplates = data.templates.filter((t) => t.difficultyLevel === "MEDIUM");
                        if (mediumTemplates.length > 0) {
                            setSelectedTemplate(mediumTemplates[0]);
                        } else if (data.templates.length > 0) {
                            setSelectedTemplate(data.templates[0]);
                        }
                    }
                }
            } catch (e) {
                Logger.error("TestJigsawCanvasPlayPage:", e);
            }
            if (!cancelled) setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (selectedTemplate) {
            setBoardPiecesMain([]);
            setBoardPiecesScatter([]);
            const n = selectedTemplate.tileSize;
            const tot = n * n;
            const key = selectedTemplate.id ?? selectedTemplate.jigsawTemplateId ?? String(n);
            setScatterPositions(buildScatterPositions(tot, n, key));
        }
    }, [selectedTemplate]);

    const handleMouseDown = useCallback((e, pieceIndex, opts) => {
        const {
            fromTray,
            fromScattered,
            boardLane,
            boardId,
            boardX,
            boardY,
            scatterX,
            scatterY,
        } = opts;
        e.preventDefault();
        if (!selectedTemplate) return;
        const cp = BOARD_SIZE / selectedTemplate.tileSize;
        let grabDx = cp / 2;
        let grabDy = cp / 2;
        if (fromScattered && scatteredRef.current && scatterX != null && scatterY != null) {
            const rect = scatteredRef.current.getBoundingClientRect();
            grabDx = e.clientX - rect.left - scatterX;
            grabDy = e.clientY - rect.top - scatterY;
        } else if (boardLane === "main" && boardRefMain.current && boardX != null && boardY != null) {
            const rect = boardRefMain.current.getBoundingClientRect();
            grabDx = e.clientX - rect.left - boardX;
            grabDy = e.clientY - rect.top - boardY;
        } else if (boardLane === "scatter" && boardRefScatter.current && boardX != null && boardY != null) {
            const rect = boardRefScatter.current.getBoundingClientRect();
            grabDx = e.clientX - rect.left - boardX;
            grabDy = e.clientY - rect.top - boardY;
        }
        setDragging({
            pieceIndex,
            x: e.clientX,
            y: e.clientY,
            fromTray: !!fromTray,
            fromScattered: !!fromScattered,
            boardLane: boardLane ?? null,
            boardId: boardId ?? null,
            grabDx,
            grabDy,
        });
    }, [selectedTemplate]);

    useEffect(() => {
        if (!dragging || !selectedTemplate) return;
        const gridN = selectedTemplate.tileSize;
        const cellPxN = BOARD_SIZE / gridN;
        const tabMargin = cellPxN * 0.35;

        const inBoard = (clientX, clientY, ref) => {
            if (!ref?.current) return false;
            const r = ref.current.getBoundingClientRect();
            const x = clientX - r.left;
            const y = clientY - r.top;
            return x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE;
        };

        const inRect = (clientX, clientY, ref) => {
            if (!ref?.current) return false;
            const r = ref.current.getBoundingClientRect();
            return clientX >= r.left && clientX < r.right && clientY >= r.top && clientY < r.bottom;
        };

        const applySnap = (nx, ny, pIndex) => {
            const homeX = (pIndex % gridN) * cellPxN;
            const homeY = Math.floor(pIndex / gridN) * cellPxN;
            const snapTol = cellPxN * 0.38;
            let x = nx;
            let y = ny;
            if (Math.abs(x - homeX) <= snapTol && Math.abs(y - homeY) <= snapTol) {
                x = homeX;
                y = homeY;
            }
            return { x, y };
        };

        const onMove = (e) => setDragging((p) => (p ? { ...p, x: e.clientX, y: e.clientY } : null));
        const onUp = (e) => {
            const { pieceIndex, fromTray, fromScattered, boardLane, boardId, grabDx, grabDy } = dragging;
            const cx = e.clientX;
            const cy = e.clientY;

            const inMain = inBoard(cx, cy, boardRefMain);
            const inScatter = inBoard(cx, cy, boardRefScatter);

            const placeOnBoard = (ref, setBoard) => {
                if (!ref.current) return;
                const rect = ref.current.getBoundingClientRect();
                let nx = cx - rect.left - grabDx;
                let ny = cy - rect.top - grabDy;
                nx = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPxN + tabMargin, nx));
                ny = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPxN + tabMargin, ny));
                const snapped = applySnap(nx, ny, pieceIndex);
                setBoard((prev) => [...prev, { id: newBoardPieceId(), pieceIndex, x: snapped.x, y: snapped.y }]);
            };

            const moveOnBoard = (setBoard) => {
                if (!boardId) return;
                const ref = boardLane === "main" ? boardRefMain : boardRefScatter;
                if (!ref.current) return;
                const rect = ref.current.getBoundingClientRect();
                let nx = cx - rect.left - grabDx;
                let ny = cy - rect.top - grabDy;
                nx = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPxN + tabMargin, nx));
                ny = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPxN + tabMargin, ny));
                const snapped = applySnap(nx, ny, pieceIndex);
                setBoard((prev) => prev.map((b) => (b.id === boardId ? { ...b, x: snapped.x, y: snapped.y } : b)));
            };

            if (inMain && boardRefMain.current) {
                if (fromTray) {
                    placeOnBoard(boardRefMain, setBoardPiecesMain);
                } else if (boardLane === "main" && boardId) {
                    moveOnBoard(setBoardPiecesMain);
                }
                setDragging(null);
                return;
            }

            if (inScatter && boardRefScatter.current) {
                if (fromScattered) {
                    placeOnBoard(boardRefScatter, setBoardPiecesScatter);
                } else if (boardLane === "scatter" && boardId) {
                    moveOnBoard(setBoardPiecesScatter);
                }
                setDragging(null);
                return;
            }

            if (
                fromScattered &&
                !inScatter &&
                inRect(cx, cy, scatteredRef) &&
                scatteredRef.current
            ) {
                const rect = scatteredRef.current.getBoundingClientRect();
                let nx = cx - rect.left - grabDx;
                let ny = cy - rect.top - grabDy;
                const maxX = Math.max(0, BOARD_SIZE - cellPxN);
                const maxY = Math.max(0, BOARD_SIZE - cellPxN);
                nx = Math.max(0, Math.min(maxX, nx));
                ny = Math.max(0, Math.min(maxY, ny));
                setScatterPositions((prev) => ({
                    ...prev,
                    [pieceIndex]: { x: nx, y: ny },
                }));
                setDragging(null);
                return;
            }

            if (!fromTray && !fromScattered && boardId) {
                if (boardLane === "main" && inRect(cx, cy, trayRef)) {
                    setBoardPiecesMain((prev) => prev.filter((b) => b.id !== boardId));
                }
                if (boardLane === "scatter" && inRect(cx, cy, scatteredRef)) {
                    setBoardPiecesScatter((prev) => prev.filter((b) => b.id !== boardId));
                }
            }

            setDragging(null);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [dragging, selectedTemplate]);

    const handleResetMain = useCallback(() => setBoardPiecesMain([]), []);
    const handleResetScatter = useCallback(() => setBoardPiecesScatter([]), []);

    const getTemplatesByDifficulty = () => {
        if (!group?.templates) return {};
        return group.templates.reduce((acc, template) => {
            const difficulty = template.difficultyLevel || "UNKNOWN";
            if (!acc[difficulty]) acc[difficulty] = [];
            acc[difficulty].push(template);
            return acc;
        }, {});
    };

    const story = group?.story ?? null;
    const imageUrl = getImageUrl(story);
    const title = getStoryTitle(story);
    const templates = group?.templates ?? [];
    const templatesByDifficulty = getTemplatesByDifficulty();

    const grid = selectedTemplate?.tileSize || 0;
    const cellPx = grid > 0 ? BOARD_SIZE / grid : 0;
    const total = grid * grid;
    const placedCountMain = boardPiecesMain.length;
    const placedCountScatter = boardPiecesScatter.length;
    const tol = cellPx * 0.22;
    const solvedFor = (pieces) =>
        total > 0 &&
        pieces.length === total &&
        pieces.every((b) => {
            const hx = (b.pieceIndex % grid) * cellPx;
            const hy = Math.floor(b.pieceIndex / grid) * cellPx;
            return Math.abs(b.x - hx) < tol && Math.abs(b.y - hy) < tol;
        });
    const isSolvedMain = solvedFor(boardPiecesMain);
    const isSolvedScatter = solvedFor(boardPiecesScatter);
    const piecesGridTray = Array.from({ length: total }, (_, i) => i).filter(
        (p) => !boardPiecesMain.some((b) => b.pieceIndex === p),
    );
    const piecesScatteredTray = Array.from({ length: total }, (_, i) => i).filter(
        (p) => !boardPiecesScatter.some((b) => b.pieceIndex === p),
    );

    const paths = selectedTemplate?.piecePaths?.pieces;
    const svgPathFallback = selectedTemplate?.svgPath;

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1700, mx: "auto" }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/system/anything")} sx={{ mb: 2 }}>
                Back
            </Button>
            <Typography variant="h5" gutterBottom>
                Test Jigsaw canvas
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Story ID <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>{TEST_JIGSAW_CANVAS_STORY_ID}</Typography>
                {" — "}public play:{" "}
                <Link component={RouterLink} to={PUBLIC_PLAY_PATH}>
                    {PUBLIC_PLAY_PATH}
                </Link>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Isolated copies live under <Typography component="span" variant="caption" sx={{ fontFamily: "monospace" }}>src/admin/jigsaw-test/</Typography> (Test* files) for future experiments without touching production jigsaw pages.
            </Typography>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : !group ? (
                <Typography color="text.secondary">Puzzle not found.</Typography>
            ) : (
                <>
                    <Typography variant="h6" gutterBottom>
                        {title || "Jigsaw Puzzle"}
                    </Typography>

                    {Object.keys(templatesByDifficulty).length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Typography variant="h6" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                        <Chip label="EASY" color="success" variant="filled" size="small" />
                                    </Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        {(templatesByDifficulty.EASY || []).map((template) => (
                                            <TestTemplateCard
                                                key={template.id}
                                                template={template}
                                                selectedTemplate={selectedTemplate}
                                                setSelectedTemplate={setSelectedTemplate}
                                                difficultyColor="success"
                                            />
                                        ))}
                                    </Box>
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography variant="h6" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                        <Chip label="MEDIUM" color="warning" variant="filled" size="small" />
                                    </Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        {(templatesByDifficulty.MEDIUM || []).map((template) => (
                                            <TestTemplateCard
                                                key={template.id}
                                                template={template}
                                                selectedTemplate={selectedTemplate}
                                                setSelectedTemplate={setSelectedTemplate}
                                                difficultyColor="warning"
                                            />
                                        ))}
                                    </Box>
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography variant="h6" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                        <Chip label="HARD" color="error" variant="filled" size="small" />
                                    </Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        {(templatesByDifficulty.HARD || []).map((template) => (
                                            <TestTemplateCard
                                                key={template.id}
                                                template={template}
                                                selectedTemplate={selectedTemplate}
                                                setSelectedTemplate={setSelectedTemplate}
                                                difficultyColor="error"
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {templates.length} template{templates.length !== 1 ? "s" : ""} available.
                        {selectedTemplate && ` Playing ${grid}×${grid} puzzle.`}
                    </Typography>

                    {selectedTemplate && paths && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            <Typography variant="subtitle1" color="text.secondary">
                                Two lanes — each uses the full piece set on its own board (grid tray vs scattered pool).
                            </Typography>
                            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Typography variant="subtitle2">Lane 1 — CanvasPieces + CanvasBoard</Typography>
                                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                                        <TestCanvasPieces
                                            trayRef={trayRef}
                                            piecesNotOnBoard={piecesGridTray}
                                            grid={grid}
                                            imageUrl={imageUrl}
                                            paths={paths}
                                            svgPathFallback={svgPathFallback}
                                            dragging={dragging}
                                            onTrayPieceMouseDown={(e, pieceIndex) =>
                                                handleMouseDown(e, pieceIndex, { fromTray: true })
                                            }
                                        />
                                        <TestCanvasBoard
                                            boardRef={boardRefMain}
                                            boardPieces={boardPiecesMain}
                                            grid={grid}
                                            imageUrl={imageUrl}
                                            paths={paths}
                                            svgPathFallback={svgPathFallback}
                                            dragging={dragging}
                                            headerTitle="Grid board"
                                            showDragOutline={Boolean(
                                                dragging && (dragging.fromTray || dragging.boardLane === "main"),
                                            )}
                                            placedCount={placedCountMain}
                                            total={total}
                                            isSolved={isSolvedMain}
                                            onReset={handleResetMain}
                                            onBoardPieceMouseDown={(e, pieceIndex, meta) =>
                                                handleMouseDown(e, pieceIndex, { boardLane: "main", ...meta })
                                            }
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Typography variant="subtitle2">Lane 2 — CanvasScattered + CanvasBoard</Typography>
                                    <TestCanvasScatteredPair
                                        boardRef={boardRefScatter}
                                        boardPieces={boardPiecesScatter}
                                        scatteredRef={scatteredRef}
                                        piecesNotOnBoard={piecesScatteredTray}
                                        scatterPositions={scatterPositions}
                                        grid={grid}
                                        imageUrl={imageUrl}
                                        paths={paths}
                                        svgPathFallback={svgPathFallback}
                                        dragging={dragging}
                                        showBoardDragOutline={Boolean(
                                            dragging && (dragging.fromScattered || dragging.boardLane === "scatter"),
                                        )}
                                        placedCount={placedCountScatter}
                                        total={total}
                                        isSolved={isSolvedScatter}
                                        onResetBoard={handleResetScatter}
                                        onBoardPieceMouseDown={(e, pieceIndex, meta) =>
                                            handleMouseDown(e, pieceIndex, { boardLane: "scatter", ...meta })
                                        }
                                        onScatteredPieceMouseDown={(e, pieceIndex, meta) =>
                                            handleMouseDown(e, pieceIndex, { fromScattered: true, ...meta })
                                        }
                                    />
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {dragging && selectedTemplate && paths && (
                        <div
                            style={{
                                position: "fixed",
                                left: dragging.x - dragging.grabDx,
                                top: dragging.y - dragging.grabDy,
                                pointerEvents: "none",
                                zIndex: 9999,
                            }}
                        >
                            <PieceVisual
                                pieceIndex={dragging.pieceIndex}
                                imageUrl={imageUrl}
                                grid={grid}
                                paths={paths}
                                showGridCoords={Boolean(dragging.fromScattered || dragging.boardLane === "scatter")}
                            />
                        </div>
                    )}
                </>
            )}
        </Box>
    );
}
